/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Menu Utils
 */

import _ from 'lodash';
import { MenuDataItem } from '@ant-design/pro-components';

import { getMenuIcon } from 'components/app/widgets/MenuIcon';
import { DEFAULT_SECTION_PROPS } from 'configs/menu';
import { ENTITY, ROUTES } from 'configs/routes';
import { allowAccess, isCodeExplorerStandalone } from './app';

interface IMeta {
	config: Menu.Item[];
	userRoles: string[];
	isRootSection?: boolean;
	isExtraRoute?: boolean;
	isSettingRoute?: boolean;
	setupInfo: App.SetupInfo;
}

/**
 * Transform Path Regex
 */
const TRANSFORM_PATH_REGEX = /\/:.*\??/;

/**
 * Transform Path
 * @param item - Menu Item
 * @returns Menu Item
 */
const transformPath = (item: Menu.Item): Menu.Item => {
	// remove dynamic path parameter from the path. eg: /:id ,/:tab?
	return { ...item, path: _.replace(item?.path, TRANSFORM_PATH_REGEX, '') };
};

/**
 * Prepare Menu Config
 */
// TODO hide top section if no child routes
const prepareMenuConfig = ({
	setupInfo,
	config,
	userRoles,
	isExtraRoute = false,
	isRootSection = false,
	isSettingRoute = false,
}: IMeta): {
	data: Menu.Item[];
	menus: MenuDataItem[];
	pages: Menu.Item[];
	search: Menu.Item[];
	openKeys: string[];
} => {
	// routes data for pro layout
	const data: Menu.Item[] = [];
	// menus renderer data for pro layout
	const menus: MenuDataItem[] = [];
	// routes / pages for react router
	const pages: Menu.Item[] = [];
	// search options
	const search: Menu.Item[] = [];
	// open keys
	const openKeys: string[] = [];

	// iterate over config
	// TODO - add feature check to support code explorer as a standalone feature
	_.forEach(config, (item: Menu.Item) => {
		item.isSettingRoute = isSettingRoute;
		const isHeaderSection = isRootSection;
		const isSiderSection = !isHeaderSection && !_.isEmpty(item?.routes);
		const isPage = !isHeaderSection && !isSiderSection && !isExtraRoute;
		// Gate by feature flag first — if explicitly disabled by the feature API, skip entirely
		if (item?.enabled === false) {
			return;
		}
		const isUnauthorized = !allowAccess(userRoles, item?.roles);
		if (isUnauthorized) {
			item.component = DEFAULT_SECTION_PROPS.component;
			item.isUnauthorized = isUnauthorized;
		}

		/**
		 * pro layout auto selection of selected menu item doesn't support dynamic path parameter
		 * trimming the dynamic path parameter from the path for data & menu is required
		 */

		if (isExtraRoute) {
			item.isExtraRoute = true;

			// update dataItem by removing routes from item & trim path
			const dataItem = transformPath(_.omit(item, 'routes'));
			data.push(dataItem);

			// update menuItem by removing routes from dataItem
			menus.push({ ..._.omit(dataItem, 'routes'), icon: getMenuIcon(dataItem?.icon) });

			// just push page Item
			pages.push(item);

			// add to search options if not id based route
			if (!item.search?.disable) {
				search.push(item);
			}
		}
		//
		// TODO - remove section if no routes & check payment addon
		if (isHeaderSection || isSiderSection) {
			const trimmedItem = transformPath(item);
			const children = prepareMenuConfig({
				setupInfo,
				config: item.routes,
				userRoles,
				isSettingRoute,
			});
			// Skip section if no accessible children
			const hideSection = _.isEmpty(children.menus) || _.every(children.pages, (page) => page.isUnauthorized);
			// update dataItem with path trimmed section & routes data
			const dataItem = { ...trimmedItem, routes: children.data };
			data.push(dataItem);

			if (!hideSection) {
				// update sectionItem with path trimmed item & children , replace routes with children
				const sectionItem: MenuDataItem = {
					..._.omit(trimmedItem, 'routes'),
					icon: getMenuIcon(trimmedItem.icon),
					children: children.menus,
				};
				menus.push(sectionItem);
				// just push child pages to search options
				search.push(...children.search);
			}

			// update pageItem with redirectRoute
			const pageItem = { ..._.omit(item, 'routes'), redirectRoute: _.first(children.pages), isHeaderSection, isSiderSection };
			pages.push(...children.pages, pageItem);
		}
		//
		else if (isPage) {
			const trimmedItem = transformPath(item);
			const hasExtraRoutes = !_.isEmpty(item.extraRoutes);
			const children = prepareMenuConfig({
				setupInfo,
				config: item.extraRoutes,
				userRoles,
				isExtraRoute: true,
				isSettingRoute,
			});

			// update dataItem with path trimmed page & extraRoutes data
			const dataItem = trimmedItem;
			if (hasExtraRoutes) {
				dataItem.extraRoutes = children.data;
			}
			data.push(dataItem);

			// update menuItem with path trimmed page & extraRoutes , add hideChildrenInMenu & children
			const menuItem: MenuDataItem = {
				..._.omit(trimmedItem, ['extraRoutes', 'routes']),
				icon: getMenuIcon(trimmedItem?.icon),
				children: children.menus,
				hideInMenu: isUnauthorized || item.hideInMenu,
				hideChildrenInMenu: hasExtraRoutes,
			};
			menus.push(menuItem);

			// add isPage to pageItem & push to pages along with child routes
			const pageItem = { ..._.omit(item, ['extraRoutes', 'routes']), isPage: true };
			pages.push(pageItem, ...children.pages);

			// add to search options if not id based route
			// TODO - check required path param
			if (!item.search?.disable) {
				search.push(item);
			}
			search.push(...children.search);
		}
	});

	// Landing for root = '/' & settings = '/settings'
	if (isRootSection) {
		let landingPage: Menu.Item;
		// TODO : temporary fix for code explorer standalone tenant, cleanup soon as a hotfix
		// if the tenant is a code explorer only tenant, add code explorer as landing page, only for app routes
		if (isCodeExplorerStandalone(setupInfo) && !isSettingRoute) {
			landingPage = _.find(pages, { entity: ENTITY.codeExplorer.key });
		}
		// default landing page: first real, accessible leaf page (skips section pageItems whose children are
		//  all gated off, and skips unauthorized leaves)
		else {
			landingPage = _.find(pages, (p) => !!p?.isPage && !p?.isUnauthorized);
		}
		// if (isRootSection && firstPage?.path) { // TODO : remove this after above cleanup
		if (landingPage?.path) {
			pages.push({
				..._.omit(landingPage, ['routes', 'extraRoutes']),
				key: `landing-${landingPage.key}`,
				name: landingPage.name,
				path: ROUTES.home.landing,
				component: landingPage.component,
				isSettingRoute,
				redirectRoute: landingPage,
			});
		}

		// add open key for pro layout
		_.forEach(pages, (page) => {
			if (page.isHeaderSection || page.isSiderSection) {
				openKeys.push(page.key);
			}
		});
	}
	return { data, menus, pages, search, openKeys };
};

export default prepareMenuConfig;
